/**
 * Affiliate param relay.
 *
 * Captures an allowlisted set of affiliate / campaign query params from the
 * storefront URL into sessionStorage, then appends them to any outbound link
 * or form that targets the Ascension web app. The app's own tracking script
 * (Linkjolt) reads the params from the URL it receives and passes them to
 * Stripe Checkout as `client_reference_id`.
 *
 * Deliberate constraints:
 * - sessionStorage only (origin + tab scoped, never sent to a server).
 *   No cookies, no localStorage, nothing Consentmo has to classify.
 * - Only allowlisted param names are relayed. Arbitrary query params are not.
 * - Outbound links are matched by hostname, not by a specific CTA, so new
 *   buttons added by marketers are covered automatically.
 * - Params a link already carries are never overwritten.
 * - Same-origin links that open in a new tab also receive the params, so the
 *   new tab can re-capture them from its own URL (sessionStorage is not
 *   shared with a noopener tab, and theme CTAs open in new tabs by default).
 * - Storage access is wrapped in try/catch and fails silent; an in-memory
 *   fallback keeps same-page links working when storage is unavailable.
 *
 * Verification:
 *   /alto?tc=test123&utm_source=blog → browse two more pages → click any app
 *   link → app URL carries tc=test123&utm_source=blog.
 */
(function affiliateRelay() {
  /** Hostnames whose outbound links receive the relayed params. */
  const APP_HOSTS = new Set(['app.ascensionpress.com', 'staging-ascension-web.vercel.app']);

  /**
   * Exact param names that are captured and relayed. Linkjolt's redirect lands
   * with tc (tracking code), aff (affiliate id) and cid (click id).
   */
  const RELAY_PARAMS = new Set([
    'tc',
    'aff',
    'cid',
    'ref',
    'via',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ]);

  const STORAGE_KEY = 'ascension:affiliate';

  /**
   * In-memory copy of the relayed params. Mirrors sessionStorage and serves as
   * the fallback when storage is unavailable (e.g. Safari private mode).
   * @type {Record<string, string>}
   */
  let memory = {};

  /**
   * Reads the stored params from sessionStorage.
   * @returns {Record<string, string>}
   */
  function readStored() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      /** @type {Record<string, string>} */
      const result = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (RELAY_PARAMS.has(key) && typeof value === 'string') result[key] = value;
      }
      return result;
    } catch {
      return {};
    }
  }

  /**
   * Persists the params to sessionStorage. Fails silent.
   * @param {Record<string, string>} params
   */
  function writeStored(params) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    } catch {
      // Storage unavailable or full; memory fallback still applies.
    }
  }

  /**
   * Captures allowlisted params from the current URL and merges them into
   * storage. Last write wins per key.
   */
  function capture() {
    const incoming = new URLSearchParams(window.location.search);
    /** @type {Record<string, string>} */
    const captured = {};
    for (const [key, value] of incoming) {
      if (RELAY_PARAMS.has(key) && value !== '') captured[key] = value;
    }

    memory = readStored();
    if (Object.keys(captured).length === 0) return;

    memory = { ...memory, ...captured };
    writeStored(memory);
  }

  /**
   * Parses an href relative to the current document.
   * @param {string | null} href
   * @returns {URL | null}
   */
  function parseUrl(href) {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url;
    } catch {
      return null;
    }
  }

  /**
   * Appends stored params the URL does not already carry.
   * @param {URL} url
   * @returns {boolean} Whether the URL was modified.
   */
  function appendParams(url) {
    let changed = false;
    for (const [key, value] of Object.entries(memory)) {
      if (url.searchParams.get(key)) continue;
      url.searchParams.set(key, value);
      changed = true;
    }
    return changed;
  }

  /**
   * Whether a URL should receive the relayed params: any link to an app host,
   * or a same-origin link that opens in a new tab (so the new tab re-captures).
   * @param {URL} url
   * @param {Element} anchor
   * @returns {boolean}
   */
  function shouldRelay(url, anchor) {
    if (APP_HOSTS.has(url.hostname)) return true;
    return url.origin === window.location.origin && anchor.getAttribute('target') === '_blank';
  }

  /**
   * Rewrites an anchor's href if it targets an app host or opens a same-origin
   * page in a new tab.
   * @param {Element} anchor
   */
  function rewriteAnchor(anchor) {
    const url = parseUrl(anchor.getAttribute('href'));
    if (!url || !shouldRelay(url, anchor)) return;
    if (appendParams(url)) anchor.setAttribute('href', url.toString());
  }

  /**
   * Rewrites a form if it targets an app host. GET forms drop the action's
   * query string on submit, so params are added as hidden inputs instead.
   * The submit button's formaction / formmethod take precedence when set.
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} submitter
   */
  function rewriteForm(form, submitter) {
    const action = submitter?.getAttribute('formaction') ?? form.getAttribute('action');
    const method = submitter?.getAttribute('formmethod') ?? form.method;
    const url = parseUrl(action);
    if (!url || !APP_HOSTS.has(url.hostname)) return;

    if (method.toLowerCase() === 'get') {
      for (const [key, value] of Object.entries(memory)) {
        if (form.elements.namedItem(key)) continue;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.append(input);
      }
      return;
    }

    if (!appendParams(url)) return;
    if (submitter?.hasAttribute('formaction')) {
      submitter.setAttribute('formaction', url.toString());
    } else {
      form.setAttribute('action', url.toString());
    }
  }

  /**
   * Delegated handler for link activation. Runs in the capture phase so the
   * href is rewritten before any other handler or the browser reads it.
   * Listens on pointerdown as well as click so middle-click and "open in new
   * tab" pick up the params too. Rewrites are idempotent.
   * @param {Event} event
   */
  function onActivate(event) {
    if (Object.keys(memory).length === 0) return;
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href]');
    if (anchor) rewriteAnchor(anchor);
  }

  /**
   * Delegated handler for form submission.
   * @param {SubmitEvent} event
   */
  function onSubmit(event) {
    if (Object.keys(memory).length === 0) return;
    if (!(event.target instanceof HTMLFormElement)) return;
    rewriteForm(event.target, event.submitter);
  }

  capture();
  document.addEventListener('pointerdown', onActivate, true);
  document.addEventListener('click', onActivate, true);
  document.addEventListener('submit', onSubmit, true);
})();
